export type AuthMode = "signup" | "signin";
export type PendingAuthKind = AuthMode | "recovery";

export interface PendingAuth {
  version: 1;
  kind: PendingAuthKind;
  email?: string;
  returnTo: string;
  createdAt: number;
  expiresAt: number;
  resendAvailableAt: number;
}

const PENDING_KEY = "bc_pending_auth_v1";
const PENDING_TTL = 24 * 60 * 60 * 1000;
const STATIC_DESTINATIONS = new Set([
  "/", "/post", "/profile", "/analytics", "/subscription", "/deposit",
  "/settings", "/admin", "/ops-command", "/wall", "/founders-wall",
  "/sell", "/marketplace",
]);

function canonicalDestination(input: unknown): string | null {
  if (typeof input !== "string" || input.length > 2048 || input !== input.trim()) return null;
  if (!input.startsWith("/") || input.startsWith("//") || input.includes("\\")
    || Array.from(input).some(character => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127)) return null;
  // Return destinations are review pages, never commands. Drop all queries and fragments,
  // including payment status, checkout IDs, auth tokens, and nested redirect parameters.
  const path = input.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
  if (STATIC_DESTINATIONS.has(path)) return path;
  if (/^\/(?:bounty|edit|profile|wall|listing)\/[a-zA-Z0-9_-]{1,128}$/.test(path)) return path;
  return null;
}

export function sanitizeReturnTo(input: unknown, fallback = "/"): string {
  return canonicalDestination(input) ?? canonicalDestination(fallback) ?? "/";
}

export function buildAuthUrl(returnTo = "/post", mode: AuthMode = "signup"): string {
  const query = new URLSearchParams({ mode: mode === "signin" ? "signin" : "signup", returnTo: sanitizeReturnTo(returnTo) });
  return `/auth?${query}`;
}

export function buildAuthCallbackUrl(returnTo = "/post", kind: "signup" | "recovery" = "signup"): string {
  // The origin is supplied by the browser, never by a caller or a query parameter.
  const url = new URL(buildAuthUrl(returnTo, "signin"), window.location.origin);
  if (kind === "recovery") url.searchParams.set("reset", "true");
  else url.searchParams.set("confirmation", "true");
  return url.href;
}

export function clearPendingAuth(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* Storage may be unavailable. */ }
}

export function getPendingAuth(now = Date.now()): PendingAuth | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value || value.version !== 1 || !["signup", "signin", "recovery"].includes(value.kind)
      || !Number.isFinite(value.createdAt) || value.createdAt > now
      || value.expiresAt !== value.createdAt + PENDING_TTL || value.expiresAt <= now
      || !Number.isFinite(value.resendAvailableAt) || value.resendAvailableAt < 0
      || (value.email !== undefined && (typeof value.email !== "string" || value.email.length > 255))) {
      clearPendingAuth();
      return null;
    }
    return {
      version: 1, kind: value.kind, email: value.email,
      returnTo: sanitizeReturnTo(value.returnTo), createdAt: value.createdAt,
      expiresAt: value.expiresAt, resendAvailableAt: value.resendAvailableAt,
    };
  } catch {
    clearPendingAuth();
    return null;
  }
}

export function setPendingAuth(
  input: { kind: PendingAuthKind; returnTo?: string; email?: string; createdAt?: number; resendAvailableAt?: number },
  now = Date.now(),
): PendingAuth {
  const createdAt = Number.isFinite(input.createdAt) && input.createdAt <= now
    && input.createdAt > now - PENDING_TTL ? input.createdAt : now;
  // Explicitly pick fields: never persist credentials, tokens, raw URLs, or provider responses.
  const pending: PendingAuth = {
    version: 1,
    kind: input.kind,
    ...(typeof input.email === "string" ? { email: input.email.trim().slice(0, 255) } : {}),
    returnTo: sanitizeReturnTo(input.returnTo, "/post"),
    createdAt,
    expiresAt: createdAt + PENDING_TTL,
    resendAvailableAt: Number.isFinite(input.resendAvailableAt) ? Math.max(0, input.resendAvailableAt) : 0,
  };
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(pending)); } catch { /* Keep the returned state in memory. */ }
  return pending;
}

export function getAuthRetryAt(error?: unknown, now = Date.now()): number {
  const value = error && typeof error === "object" ? error as Record<string, unknown> : {};
  const headers = value.headers as { get?: (name: string) => string | null } | undefined;
  const retry = value.retryAfter ?? value.retry_after ?? headers?.get?.("Retry-After");
  const seconds = typeof retry === "number" || typeof retry === "string" ? Number(retry) : NaN;
  const date = typeof retry === "string" && !Number.isFinite(seconds) ? Date.parse(retry) : NaN;
  // Supabase Auth errors can expose a wait in the message without exposing response headers.
  const match = typeof value.message === "string" ? value.message.match(/(?:after|in)\s+(\d+)\s+seconds?/i) : null;
  return Math.max(now + 60_000, Number.isFinite(seconds) ? now + seconds * 1000 : 0,
    Number.isFinite(date) ? date : 0, match ? now + Number(match[1]) * 1000 : 0);
}
